/**
 * Transcribes one rapid-fire clip: the same `/api/transcribe` POST-then-poll flow as an ordinary
 * open-text answer, but writing into no shared state — it resolves with the text and the caller
 * delivers it keyed by question id.
 */

const POLL_INTERVAL_MS = 1000;
const MAX_POLL_ATTEMPTS = 30; // matching SurveySession's own transcription poll
/** Wall clock for the whole flow. MAX_POLL_ATTEMPTS bounds ITERATIONS, which is not a time. */
const MAX_TRANSCRIBE_MS = 30000;
/** Per-request deadline: a `fetch` has none, and a section bound to this answer waits on it
 *  (ADR-0005), so a promise that never settles is a stuck respondent. */
const REQUEST_TIMEOUT_MS = 15000;

function fileExtensionFor(mimeType: string): string {
  if (mimeType.includes('webm')) return '.webm';
  if (mimeType.includes('mp4')) return '.mp4';
  if (mimeType.includes('wav')) return '.wav';
  if (mimeType.includes('mp3')) return '.mp3';
  if (mimeType.includes('m4a')) return '.m4a';
  return '.webm';
}

export async function transcribeRapidFireClip(
  sessionId: number,
  questionId: string,
  blob: Blob,
): Promise<string> {
  const mimeType = blob.type || 'audio/webm';
  const formData = new FormData();
  formData.append('audio', blob, `recording${fileExtensionFor(mimeType)}`);
  formData.append('context_type', 'survey');
  formData.append(
    'context_metadata',
    JSON.stringify({
      session_id: String(sessionId),
      question_id: String(questionId),
    }),
  );

  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/transcribe`, {
    method: 'POST',
    body: formData,
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
  if (!response.ok) {
    throw new Error(`Transcription failed: ${response.status}`);
  }

  const result = await response.json();
  if (result.status !== 'processing' || !result.task_id) {
    throw new Error(result.error || 'Transcription failed - no task ID returned');
  }

  const deadline = Date.now() + MAX_TRANSCRIBE_MS;
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS && Date.now() < deadline; attempt += 1) {
    const statusResponse = await fetch(
      `${import.meta.env.VITE_API_URL}/api/transcribe/status/${result.task_id}`,
      { signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) },
    );
    if (!statusResponse.ok) {
      throw new Error('Failed to check transcription status');
    }
    const statusResult = await statusResponse.json();
    if (statusResult.status === 'completed') return statusResult.text || '';
    if (statusResult.status === 'failed') {
      throw new Error(statusResult.error || 'Transcription failed');
    }
    await new Promise((resolve) => {
      setTimeout(resolve, POLL_INTERVAL_MS);
    });
  }
  throw new Error('Transcription timeout');
}
